/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { User, OrdenProduccion, DisposicionProducto, BackupRecord } from '../types';

export function generateUsername(fullName: string): string {
  const parts = fullName.trim().split(/\s+/).filter(p => p.length > 0);
  if (parts.length === 0) return '';
  const firstName = parts[0];
  
  let surname = '';
  if (parts.length === 1) {
    surname = '';
  } else if (parts.length === 2) {
    surname = parts[1];
  } else if (parts.length === 3) {
    // Check if middle word is a common middle name, otherwise assume it is first surname
    const commonMiddleNames = [
      'carlos', 'pablo', 'maria', 'josé', 'jose', 'andrés', 'andres', 'felipe', 'ignacio', 
      'david', 'guillermo', 'luis', 'antonio', 'fernando', 'manuel', 'francisco', 'javier', 
      'alejandro', 'alberto', 'miguel', 'angel', 'ángel', 'paola', 'andrea', 'eduardo', 'rene'
    ];
    if (commonMiddleNames.includes(parts[1].toLowerCase())) {
      surname = parts[2];
    } else {
      surname = parts[1];
    }
  } else {
    // 4 or more words: e.g. "Juan Pablo Gomez Perez" -> Gomez is first surname (index 2)
    const commonMiddleNames = [
      'carlos', 'pablo', 'maria', 'josé', 'jose', 'andrés', 'andres', 'felipe', 'ignacio', 
      'david', 'guillermo', 'luis', 'antonio', 'fernando', 'manuel', 'francisco', 'javier', 
      'alejandro', 'alberto', 'miguel', 'angel', 'ángel', 'paola', 'andrea', 'eduardo', 'rene',
      'fernanda', 'camila', 'sofia', 'sofía', 'elena', 'isabel', 'lucia', 'lucía', 'beatriz', 'patricia'
    ];
    if (commonMiddleNames.includes(parts[1].toLowerCase())) {
      surname = parts[2];
    } else {
      surname = parts[1];
    }
  }
  
  const cleanFirst = firstName.charAt(0).toUpperCase() + firstName.slice(1).toLowerCase();
  const cleanSurname = surname ? (surname.charAt(0).toUpperCase() + surname.slice(1).toLowerCase()) : '';
  return cleanSurname ? `${cleanFirst}.${cleanSurname}` : cleanFirst;
}

export function generatePredefinedSignature(type: 'maria' | 'natividad' | string, name?: string): string {
  if (typeof document === 'undefined') return '';
  try {
    const canvas = document.createElement('canvas');
    canvas.width = 300;
    canvas.height = 150;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.fillStyle = 'rgba(0,0,0,0)'; // transparent background
      ctx.fillRect(0, 0, 300, 150);
      
      if (type === 'maria') {
        // Draw Maria's beautiful cursive signature loop
        ctx.strokeStyle = '#020617'; // elegant deep charcoal/black ink
        ctx.lineWidth = 2.4;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';

        // Major elegant starting loop
        ctx.beginPath();
        ctx.moveTo(35, 95);
        ctx.bezierCurveTo(20, 20, 75, 0, 90, 40);
        ctx.bezierCurveTo(75, 75, 48, 115, 38, 135);
        ctx.bezierCurveTo(28, 155, 45, 135, 53, 115);
        ctx.stroke();

        ctx.beginPath();
        ctx.moveTo(53, 115);
        ctx.bezierCurveTo(63, 40, 78, 45, 83, 100);
        ctx.bezierCurveTo(88, 40, 98, 45, 103, 100);
        ctx.stroke();

        ctx.beginPath();
        ctx.moveTo(103, 100);
        ctx.bezierCurveTo(108, 90, 118, 90, 118, 100);
        ctx.bezierCurveTo(118, 110, 108, 110, 118, 105);
        ctx.lineTo(128, 95); ctx.lineTo(133, 95); ctx.lineTo(133, 105);
        ctx.lineTo(143, 95); ctx.lineTo(143, 105);
        ctx.bezierCurveTo(148, 95, 158, 95, 158, 105);
        ctx.stroke();

        ctx.beginPath();
        ctx.moveTo(158, 105);
        ctx.bezierCurveTo(185, 20, 215, 10, 220, 40);
        ctx.bezierCurveTo(195, 85, 180, 115, 190, 110);
        ctx.stroke();

        ctx.beginPath();
        ctx.moveTo(190, 110);
        ctx.bezierCurveTo(195, 100, 200, 100, 200, 110);
        ctx.bezierCurveTo(205, 100, 213, 100, 213, 107);
        ctx.bezierCurveTo(220, 65, 230, 65, 227, 110);
        ctx.bezierCurveTo(230, 100, 240, 100, 240, 110);
        ctx.bezierCurveTo(245, 115, 250, 140, 240, 145);
        ctx.bezierCurveTo(230, 145, 243, 120, 255, 110);
        ctx.lineTo(265, 100); ctx.lineTo(270, 110);
        ctx.bezierCurveTo(275, 100, 285, 100, 285, 110);
        ctx.stroke();

        ctx.beginPath();
        ctx.lineWidth = 1.9;
        ctx.moveTo(30, 140);
        ctx.bezierCurveTo(105, 155, 205, 150, 295, 120);
        ctx.stroke();
      } else if (type === 'natividad') {
        // Draw Natividad's clean calligraphic print signature "Natividad R. C."
        ctx.strokeStyle = '#020617';
        ctx.lineWidth = 2.5;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';

        const drawLetterPath = (points: [number, number][]) => {
          ctx.beginPath();
          ctx.moveTo(points[0][0], points[0][1]);
          for (let i = 1; i < points.length; i++) {
            ctx.lineTo(points[i][0], points[i][1]);
          }
          ctx.stroke();
        };

        // N
        drawLetterPath([[40, 110], [40, 60], [55, 105], [55, 60]]);
        
        ctx.beginPath();
        ctx.arc(70, 95, 8, 0, Math.PI * 2);
        ctx.moveTo(78, 87);
        ctx.lineTo(78, 103);
        ctx.stroke();

        drawLetterPath([[88, 70], [88, 103]]);
        drawLetterPath([[83, 80], [93, 80]]);

        drawLetterPath([[100, 85], [100, 103]]);
        ctx.beginPath();
        ctx.arc(100, 77, 2, 0, Math.PI * 2);
        ctx.fill();

        drawLetterPath([[108, 85], [114, 103], [120, 85]]);

        drawLetterPath([[128, 85], [128, 103]]);
        ctx.beginPath();
        ctx.arc(128, 77, 2, 0, Math.PI * 2);
        ctx.fill();

        ctx.beginPath();
        ctx.arc(140, 95, 8, 0, Math.PI * 2);
        ctx.moveTo(148, 70);
        ctx.lineTo(148, 103);
        ctx.stroke();

        ctx.beginPath();
        ctx.arc(162, 95, 8, 0, Math.PI * 2);
        ctx.moveTo(170, 87);
        ctx.lineTo(170, 103);
        ctx.stroke();

        ctx.beginPath();
        ctx.arc(182, 95, 8, 0, Math.PI * 2);
        ctx.moveTo(190, 70);
        ctx.lineTo(190, 103);
        ctx.stroke();

        drawLetterPath([[215, 103], [215, 70]]);
        ctx.beginPath();
        ctx.arc(225, 82, 12, -Math.PI / 2, Math.PI / 2);
        ctx.lineTo(215, 94);
        ctx.stroke();
        drawLetterPath([[223, 94], [235, 103]]);

        ctx.beginPath();
        ctx.arc(242, 101, 2, 0, Math.PI * 2);
        ctx.fill();

        ctx.beginPath();
        ctx.arc(265, 87, 13, Math.PI / 4, 1.7 * Math.PI, true);
        ctx.stroke();

        ctx.beginPath();
        ctx.arc(282, 101, 2, 0, Math.PI * 2);
        ctx.fill();
      } else {
        // A generic beautiful cursive signature based on the name passed
        const displayName = name || type || 'Firma Autorizada';
        ctx.strokeStyle = '#020617'; // elegant deep slate/indigo ink
        ctx.lineWidth = 2.4;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        
        ctx.beginPath();
        // Cursive starting sweep
        ctx.moveTo(35, 95);
        ctx.bezierCurveTo(20, 25, 65, 15, 80, 50);
        ctx.bezierCurveTo(65, 85, 45, 120, 40, 135);
        ctx.stroke();

        ctx.beginPath();
        ctx.moveTo(40, 135);
        ctx.bezierCurveTo(60, 140, 85, 100, 115, 80);
        ctx.stroke();

        // Write name in beautiful elegant cursive hand-drawn style
        ctx.font = 'italic 20px "Georgia", "Clicker Script", cursive, serif';
        ctx.fillStyle = '#0f172a';
        ctx.fillText(displayName.split(' ')[0] + ' ' + (displayName.split(' ')[1] || ''), 70, 105);
        
        // Cursive end underline sweep
        ctx.beginPath();
        ctx.lineWidth = 1.8;
        ctx.moveTo(35, 125);
        ctx.bezierCurveTo(100, 140, 180, 135, 275, 105);
        ctx.stroke();
      }
      return canvas.toDataURL('image/png');
    }
  } catch (err) {
    console.error("Error generating predefined signature:", err);
  }
  return '';
}

export const INITIAL_USERS: User[] = [
  {
    id: 'user-administrador-unique',
    nombreCompleto: 'Juan Pablo Valencia Berrio',
    cedula: '16078935',
    correo: 'qualityprocesosyservicios@gmail.com',
    password: 'Jvalencia4@',
    cargo: 'Administrador del Sistema',
    username: 'JUAN.VALENCIA',
    estado: 'ACTIVO',
    esAdmin: true,
    modulosPermitidos: ['registro_ordenes', 'disposicion_productos', 'dashboard_productividad', 'gestion_usuarios'],
    companyId: 'company-insumedic'
  },
  {
    id: 'user-insumedic-carrero',
    nombreCompleto: 'LAURA PATRICIA CARRERO PERDOMO',
    cedula: '55178901',
    cargo: 'Auxiliar de Reempaque',
    username: 'LAURA.CARRERO',
    estado: 'ACTIVO',
    esAdmin: false,
    modulosPermitidos: ['disposicion_productos', 'registro_ordenes'],
    companyId: 'company-insumedic'
  },
  {
    id: 'user-insumedic-cechagua',
    nombreCompleto: 'MARIA ANGELICA CECHAGUA',
    cedula: '55069292',
    cargo: 'Director Técnico y de Calidad',
    username: 'MARIA.CECHAGUA',
    estado: 'ACTIVO',
    esAdmin: false,
    modulosPermitidos: ['registro_ordenes', 'disposicion_productos', 'dashboard_productividad', 'gestion_usuarios'],
    companyId: 'company-insumedic'
  },
  {
    id: 'user-insumedic-cuellar',
    nombreCompleto: 'NATIVIDAD RUGELES CUELLAR',
    cedula: '28562178',
    cargo: 'Regente Farmacéutico',
    username: 'NATIVIDAD.CUELLAR',
    estado: 'ACTIVO',
    esAdmin: false,
    modulosPermitidos: ['registro_ordenes', 'disposicion_productos', 'dashboard_productividad'],
    companyId: 'company-insumedic'
  },
  {
    id: 'user-insumedic-admin',
    nombreCompleto: 'ADMINISTRADOR DEL SISTEMA',
    cedula: '16078935',
    cargo: 'Administrador del Sistema',
    username: 'ADM.SISTEMAS',
    estado: 'ACTIVO',
    esAdmin: true,
    modulosPermitidos: ['registro_ordenes', 'disposicion_productos', 'dashboard_productividad', 'gestion_usuarios'],
    companyId: 'company-insumedic'
  },
  {
    id: 'user-insumedic-trilleras',
    nombreCompleto: 'ERNESTO TRILLERAS CASTILLO',
    cedula: '7712491',
    cargo: 'Operario logístico',
    username: 'ERNESTO.TRILLERAS',
    estado: 'ACTIVO',
    esAdmin: false,
    modulosPermitidos: ['registro_ordenes'],
    companyId: 'company-insumedic'
  }
];

export const INITIAL_ORDENES: OrdenProduccion[] = [
  {
    id: 'op-26052026-1',
    numeroOrden: 'OP-26052026-1',
    loteRP: 'RP-1',
    loteFabricante: 'LOT-BLAS-2605',
    codigoMedicamento: 'MED-TRA-050',
    nombreGenerico: 'TRAMADOL CLORHIDRATO',
    nombreComercial: 'TRAMADOL',
    concentracion: '50 MG/ML',
    formaFarmaceutica: 'SOLUCIÓN INYECTABLE',
    laboratorioFabricante: 'LABORATORIOS BLASKOV',
    fechaVencimiento: '2028-05-26',
    registroSanitario: 'INVIMA 2019M-0014522',
    cum: '19948231-1',
    tamanoLote: 150,
    manejoEspecial: 'NO',
    estadoOrden: 'Cerrada',
    elaboradoPor: 'LAURA PATRICIA CARRERO PERDOMO',
    verificadoPor: 'NATIVIDAD RUGELES CUELLAR',
    fechaRegistro: '2026-05-26T08:00:00.000Z',
    fechaInicioReempaque: '2026-05-26T08:05:00',
    fechaFinReempaque: '2026-05-26T08:15:00',
    companyId: 'company-insumedic'
  },
  {
    id: 'op-01062026-7',
    numeroOrden: 'OP-01062026-7',
    loteRP: 'RP-7',
    loteFabricante: 'LOT-NOV-0106',
    codigoMedicamento: 'MED-TIZ-002',
    nombreGenerico: 'TIZANIDINA',
    nombreComercial: 'SIRDALUD',
    concentracion: '2 MG',
    formaFarmaceutica: 'TABLETA',
    laboratorioFabricante: 'NOVARTIS',
    fechaVencimiento: '2028-06-01',
    registroSanitario: 'INVIMA 2018M-0001234',
    cum: '19901234-1',
    tamanoLote: 40,
    manejoEspecial: 'NO',
    estadoOrden: 'Cerrada',
    elaboradoPor: 'LAURA PATRICIA CARRERO PERDOMO',
    verificadoPor: 'NATIVIDAD RUGELES CUELLAR',
    fechaRegistro: '2026-06-01T20:00:00.000Z',
    fechaInicioReempaque: '2026-06-01T20:30:00',
    fechaFinReempaque: '2026-06-01T21:50:00',
    companyId: 'company-insumedic'
  },
  {
    id: 'op-01062026-9',
    numeroOrden: 'OP-01062026-9',
    loteRP: 'RP-9',
    loteFabricante: 'LOT-SIEG-0106',
    codigoMedicamento: 'MED-CAF-100',
    nombreGenerico: 'CAFEINA ERGOTAMINA',
    nombreComercial: 'FENCAFEN',
    concentracion: '100 MG / 1 MG',
    formaFarmaceutica: 'TABLETA',
    laboratorioFabricante: 'LABORATORIOS SIEGFRIED',
    fechaVencimiento: '2028-06-01',
    registroSanitario: 'INVIMA 2020M-0019231',
    cum: '19951234-2',
    tamanoLote: 30,
    manejoEspecial: 'NO',
    estadoOrden: 'Cerrada',
    elaboradoPor: 'LAURA PATRICIA CARRERO PERDOMO',
    verificadoPor: 'NATIVIDAD RUGELES CUELLAR',
    fechaRegistro: '2026-06-01T20:15:00.000Z',
    fechaInicioReempaque: '2026-06-01T20:45:00',
    fechaFinReempaque: '2026-06-01T21:52:00',
    companyId: 'company-insumedic'
  },
  {
    id: 'op-14072026-1',
    numeroOrden: 'OP-14072026-1',
    loteRP: 'RP-1',
    loteFabricante: 'LOT-14072026-1',
    codigoMedicamento: 'MED-TIZ-100',
    nombreGenerico: 'TIZANIDINA',
    nombreComercial: 'SIRDALUD',
    concentracion: '2 MG',
    formaFarmaceutica: 'TABLETA',
    laboratorioFabricante: 'NOVARTIS',
    fechaVencimiento: '2028-07-14',
    registroSanitario: 'INVIMA 2018M-0001234',
    cum: '19901234-1',
    tamanoLote: 100,
    manejoEspecial: 'NO',
    estadoOrden: 'Cerrada',
    elaboradoPor: 'LAURA PATRICIA CARRERO PERDOMO',
    verificadoPor: 'MARIA ANGELICA CECHAGUA',
    fechaRegistro: '2026-07-14T12:00:00.000Z',
    fechaInicioReempaque: '2026-07-14T12:30:00',
    fechaFinReempaque: '2026-07-14T17:35:00',
    companyId: 'company-insumedic'
  },
  {
    id: 'op-14072026-2',
    numeroOrden: 'OP-14072026-2',
    loteRP: 'RP-2',
    loteFabricante: 'LOT-14072026-2',
    codigoMedicamento: 'MED-TIZ-100',
    nombreGenerico: 'TIZANIDINA',
    nombreComercial: 'SIRDALUD',
    concentracion: '2 MG',
    formaFarmaceutica: 'TABLETA',
    laboratorioFabricante: 'NOVARTIS',
    fechaVencimiento: '2028-07-14',
    registroSanitario: 'INVIMA 2018M-0001234',
    cum: '19901234-1',
    tamanoLote: 100,
    manejoEspecial: 'NO',
    estadoOrden: 'Cerrada',
    elaboradoPor: 'LAURA PATRICIA CARRERO PERDOMO',
    verificadoPor: 'MARIA ANGELICA CECHAGUA',
    fechaRegistro: '2026-07-14T12:00:00.000Z',
    fechaInicioReempaque: '2026-07-14T12:30:00',
    fechaFinReempaque: '2026-07-14T17:35:00',
    companyId: 'company-insumedic'
  },
  {
    id: 'op-27052026-1',
    numeroOrden: 'OP-27052026-1',
    loteRP: 'RP-1',
    loteFabricante: 'LOT-PRA-2705',
    codigoMedicamento: 'MED-PRA-001',
    nombreGenerico: 'PRAZOSINA CLORHIDRATO',
    nombreComercial: 'MINIPRESS 1MG',
    concentracion: '1 MG',
    formaFarmaceutica: 'TABLETA',
    laboratorioFabricante: 'PFIZER',
    fechaVencimiento: '2028-05-27',
    registroSanitario: 'INVIMA 2019M-0018765',
    cum: '19962341-1',
    tamanoLote: 100,
    manejoEspecial: 'NO',
    estadoOrden: 'Cerrada',
    elaboradoPor: 'LAURA PATRICIA CARRERO PERDOMO',
    verificadoPor: 'JUAN PABLO VALENCIA BERRIO',
    fechaRegistro: '2026-05-27T10:00:00.000Z',
    fechaInicioReempaque: '2026-05-27T10:15:00',
    fechaFinReempaque: '2026-05-27T10:55:00',
    companyId: 'company-insumedic'
  }
];

export const INITIAL_DISPOSICIONES: DisposicionProducto[] = [
  {
    id: '2',
    ordenId: 'op-26052026-1',
    numeroOrden: 'OP-26052026-1',
    nombreMedicamento: 'TRAMADOL (TRAMADOL)',
    estadoDisposicion: 'Rechazado',
    cantidadDispuesta: 150,
    observaciones: 'QUEDARON MAL EMPACADAS',
    responsable: 'NATIVIDAD RUGELES CUELLAR',
    fechaDisposicion: '2026-05-26T08:17:34',
    companyId: 'company-insumedic'
  },
  {
    id: '4',
    ordenId: 'op-01062026-7',
    numeroOrden: 'OP-01062026-7',
    nombreMedicamento: 'TIZANIDINA (SIRDALUD)',
    estadoDisposicion: 'Aprobado',
    cantidadDispuesta: 40,
    observaciones: 'Acondicionamiento físico correcto del lote RP-7. Verificado cumple estándar asistencial.',
    responsable: 'NATIVIDAD RUGELES CUELLAR',
    fechaDisposicion: '2026-06-01T21:54:43',
    companyId: 'company-insumedic'
  },
  {
    id: '5',
    ordenId: 'op-01062026-9',
    numeroOrden: 'OP-01062026-9',
    nombreMedicamento: 'CAFEINA ERGOTAMINA (FENCAFEN)',
    estadoDisposicion: 'Aprobado',
    cantidadDispuesta: 30,
    observaciones: 'Acondicionamiento físico correcto del lote RP-9. Verificado cumple estándar asistencial.',
    responsable: 'NATIVIDAD RUGELES CUELLAR',
    fechaDisposicion: '2026-06-01T21:54:59',
    companyId: 'company-insumedic'
  },
  {
    id: '6',
    ordenId: 'op-14072026-1',
    numeroOrden: 'OP-14072026-1',
    nombreMedicamento: 'TIZANIDINA (SIRDALUD)',
    estadoDisposicion: 'Aprobado',
    cantidadDispuesta: 100,
    observaciones: 'Acondicionamiento físico correcto del lote RP-1. Verificado cumple estándar asistencial.',
    responsable: 'MARIA ANGELICA CECHAGUA',
    fechaDisposicion: '2026-07-14T17:35:00',
    companyId: 'company-insumedic'
  },
  {
    id: '7',
    ordenId: 'op-14072026-2',
    numeroOrden: 'OP-14072026-2',
    nombreMedicamento: 'TIZANIDINA (SIRDALUD)',
    estadoDisposicion: 'Aprobado',
    cantidadDispuesta: 100,
    observaciones: 'Acondicionamiento físico correcto del lote RP-2. Verificado cumple estándar asistencial.',
    responsable: 'MARIA ANGELICA CECHAGUA',
    fechaDisposicion: '2026-07-14T17:35:00',
    companyId: 'company-insumedic'
  },
  {
    id: 'disp-prazosina-1',
    ordenId: 'op-27052026-1',
    numeroOrden: 'OP-27052026-1',
    nombreMedicamento: 'PRAZOSINA CLORHIDRATO (MINIPRESS 1MG)',
    estadoDisposicion: 'Aprobado',
    cantidadDispuesta: 100,
    observaciones: 'Liberación de lote de Prazosina conforme a especificaciones de calidad y empaque.',
    responsable: 'JUAN PABLO VALENCIA BERRIO',
    fechaDisposicion: '2026-06-03T10:56:39',
    companyId: 'company-insumedic'
  }
];

export const INITIAL_BACKUPS: BackupRecord[] = [
  {
    id: 'BACKUP-AUTO-20260714-1800',
    fechaHora: '2026-07-14 18:00:00',
    timestamp: '2026-07-14T18:00:00.000Z',
    ordenesCount: INITIAL_ORDENES.length,
    disposicionesCount: INITIAL_DISPOSICIONES.length,
    usersCount: INITIAL_USERS.length,
    tipo: 'AUTOMATICA (18:00)',
    companyId: 'company-insumedic',
    createdBy: 'SYSTEM',
    schemaVersion: '3.0.0',
    hashSha256: '9f86d081884c7d659a2feaa0c55ad015a3bf4f1b2b0b822cd15d6c15b0f00a08',
    status: 'COMPLETED',
    data: {
      ordenes: INITIAL_ORDENES,
      disposiciones: INITIAL_DISPOSICIONES,
      users: INITIAL_USERS
    }
  },
  {
    id: 'BACKUP-MANUAL-20260714-1736',
    fechaHora: '2026-07-14 17:36:12',
    timestamp: '2026-07-14T17:36:12.000Z',
    ordenesCount: INITIAL_ORDENES.length,
    disposicionesCount: INITIAL_DISPOSICIONES.length,
    usersCount: INITIAL_USERS.length,
    tipo: 'MANUAL',
    companyId: 'company-insumedic',
    createdBy: 'JUAN.VALENCIA',
    schemaVersion: '3.0.0',
    hashSha256: '5e884898da28047151d0e56f8dc6292773603d0d6aabbdd62a11ef721d1542d8',
    status: 'COMPLETED',
    data: {
      ordenes: INITIAL_ORDENES,
      disposiciones: INITIAL_DISPOSICIONES,
      users: INITIAL_USERS
    }
  }
];

export const CARGOS = [
  'Administrador del Sistema',
  'Director Técnico y de Calidad',
  'Regente Farmacéutico',
  'Auxiliar de Reempaque',
  'Operario logístico'
];
